import type { ProviderSlug } from "./providers";
import type { AvailabilitySignalKind } from "./types";

export type IngestMode = "automated" | "manual_review" | "unsupported";
export type Schedule = "hourly" | "daily" | "weekly" | "manual";

export interface AdapterMeta {
  id: string;
  title: string;
  publisher: string;
  mode: IngestMode;
  schedule: Schedule;
  measures: string;
  credentials?: readonly string[];
  provider?: ProviderSlug;
  signals?: readonly AvailabilitySignalKind[];
}

// One list for the pipeline, the sources page, and the methodology page. An adapter that
// names credentials runs only when every variable is set and shows as waiting otherwise.
export const ADAPTER_META: readonly AdapterMeta[] = [
  {
    id: "aws-regions",
    title: "AWS regions and zones",
    publisher: "Amazon Web Services",
    mode: "automated",
    schedule: "daily",
    measures:
      "Region codes, names, availability zone counts, and launch dates. Geography only; no power.",
  },
  {
    id: "gcp-regions",
    title: "Google Cloud regions and zones",
    publisher: "Google Cloud",
    mode: "automated",
    schedule: "daily",
    measures:
      "Zone codes with their cities, zones per region, and region codes present in the public IP range feed. Geography only.",
  },
  {
    id: "azure-regions",
    title: "Azure regions list",
    publisher: "Microsoft",
    mode: "automated",
    schedule: "daily",
    measures:
      "Public cloud regions with availability zone counts and physical locations. Geography only.",
  },
  {
    id: "epoch-ai",
    title: "Epoch AI, AI data centers",
    publisher: "Epoch AI",
    mode: "automated",
    schedule: "daily",
    measures:
      "Estimated IT power, total power, and H100-equivalent compute for the largest AI data centers, with dated construction timelines. AI facilities only.",
  },
  {
    id: "csv-import",
    title: "Reviewed observations",
    publisher: "Cloud Atlas maintainers",
    mode: "manual_review",
    schedule: "manual",
    measures:
      "Hand-reviewed capacity, investment, land, and count observations transcribed with a citation and excerpt.",
  },
  {
    id: "azure-retail-prices",
    title: "Azure Retail Prices",
    publisher: "Microsoft",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Which VM SKUs are offered per region, and the spot to pay-as-you-go price ratio as a scarcity proxy.",
  },
  {
    id: "aws-spot-advisor",
    title: "AWS Spot Instance Advisor",
    publisher: "Amazon Web Services",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Spot interruption frequency band per instance type and region over the trailing month.",
  },
  {
    id: "news-feeds",
    title: "Provider news feeds",
    publisher: "Amazon, Google, Microsoft, Alibaba",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Power and investment figures matched by rule in the providers' own news feeds; a one-figure sentence on a tracked site is accepted, everything else waits for review.",
  },
  {
    id: "gcp-sites",
    title: "Google data center locations",
    publisher: "Google",
    mode: "automated",
    schedule: "daily",
    measures:
      "Investment to date per location page, with the year established; euro and pound figures are converted at the day's ECB reference rate; a page covering several cities or an unresolved place waits for review.",
  },
  {
    id: "azure-globe-regions",
    title: "Azure datacenter globe",
    publisher: "Microsoft",
    mode: "automated",
    schedule: "daily",
    measures:
      "Opening year per Azure region from the datacenter globe, matched to the region entity by code or name. Geography only.",
  },
  {
    id: "nscale-sites",
    title: "Nscale AI infrastructure sites",
    publisher: "Nscale",
    mode: "automated",
    schedule: "daily",
    measures:
      "Power figures in the per-site prose of Nscale's infrastructure page, by the one-figure sentence rule; everything else waits for review.",
  },
  {
    id: "osm-datacenters",
    title: "OpenStreetMap datacenter buildings",
    publisher: "OpenStreetMap contributors",
    mode: "automated",
    schedule: "daily",
    measures:
      "Buildings tagged telecom=data_center with a tracked operator, placed from the map and counted as one building each under ODbL 1.0; a building never enters a power total, and an it_power tag waits for review.",
  },
  {
    id: "gcp-gpu-zones",
    title: "Google Cloud GPU zones",
    publisher: "Google Cloud",
    mode: "automated",
    schedule: "daily",
    measures: "GPU machine types offered per zone, from the documentation. Offering only.",
  },
  {
    id: "aws-ec2-probes",
    title: "AWS placement scores and Capacity Blocks",
    publisher: "Amazon Web Services",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Spot placement score per instance type and region for 8 and 64 units, and days until the earliest 24 hour Capacity Block for p5.48xlarge and p5en.48xlarge.",
    credentials: ["AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY"],
    provider: "aws",
    signals: ["placement_score", "lead_time_days"],
  },
  {
    id: "gcp-calendar-mode",
    title: "Google Cloud calendar-mode advice",
    publisher: "Google Cloud",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Days until Compute Engine can start a 24-hour block of 1 or 8 A3 or A4 GPU VMs per region, from the calendar-mode advice API.",
    credentials: ["GCP_PROJECT", "GCP_SERVICE_ACCOUNT_JSON"],
    provider: "gcp",
    signals: ["lead_time_days"],
  },
  {
    id: "azure-placement-scores",
    title: "Azure spot placement scores",
    publisher: "Microsoft Azure",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Microsoft's High, Medium or Low Spot placement score per VM size and region for 1 and 8 instances, from the Spot Placement Scores API.",
    credentials: [
      "AZURE_TENANT_ID",
      "AZURE_CLIENT_ID",
      "AZURE_CLIENT_SECRET",
      "AZURE_SUBSCRIPTION_ID",
    ],
    provider: "azure",
    signals: ["spot_placement"],
  },
  {
    id: "oci-capacity-report",
    title: "Oracle Cloud capacity reports",
    publisher: "Oracle",
    mode: "automated",
    schedule: "hourly",
    measures:
      "Capacity report verdict per GPU shape and availability domain across the tenancy's subscribed regions.",
    credentials: ["OCI_TENANCY", "OCI_USER", "OCI_FINGERPRINT", "OCI_PRIVATE_KEY", "OCI_REGION"],
    provider: "oracle",
    signals: ["capacity_report"],
  },
  {
    id: "alibaba-available-resource",
    title: "Alibaba Cloud available resources",
    publisher: "Alibaba Cloud",
    mode: "automated",
    schedule: "hourly",
    measures: "Sell status per GPU instance type and zone from the ECS available resource check.",
    credentials: ["ALIBABA_ACCESS_KEY_ID", "ALIBABA_ACCESS_KEY_SECRET"],
    provider: "alibaba",
    signals: ["sell_status"],
  },
  {
    id: "tencent-zone-config",
    title: "Tencent Cloud zone instance configs",
    publisher: "Tencent Cloud",
    mode: "automated",
    schedule: "hourly",
    measures: "Sell status per GPU instance type and zone from the CVM zone configuration listing.",
    credentials: ["TENCENT_SECRET_ID", "TENCENT_SECRET_KEY"],
    provider: "tencent",
    signals: ["sell_status"],
  },
];

export const adapterMeta = (id: string): AdapterMeta | undefined =>
  ADAPTER_META.find((a) => a.id === id);
